import React, { useState } from 'react'
import {
    autoUpdate,
    flip,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole
} from '@floating-ui/react'

export interface WMOption {
    id: string
    text: string
    value?: any
    onClick?: () => void
}

interface IProps {
    className?: string
    render?: JSX.Element
    items?: WMOption[]
    ariaLabel?: string
}

const GAP = 5

const Menu: React.FC<IProps> = ({ className, render, items, ariaLabel }) => {
    const [isOpen, setIsOpen] = useState(false)
    const { x, y, strategy, refs, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [flip(), shift(), offset(GAP)],
        whileElementsMounted: autoUpdate
    })

    const click = useClick(context)
    const dismiss = useDismiss(context)
    const role = useRole(context)
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

    return (
        <div className={`${className} `}>
            <div
                ref={refs?.setReference}
                {...getReferenceProps()}
                className={`${className} menu-trigger cursor-pointer`}
                role={'button'}
                tabIndex={0}
                aria-expanded={isOpen}
                aria-label={ariaLabel}
                onClick={(e: any) => {
                    setIsOpen(!isOpen)
                    e.stopPropagation()
                }}
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setIsOpen(!isOpen)
                    }
                }}>
                {render}
            </div>
            {isOpen && (
                <div
                    ref={refs?.setFloating}
                    style={{
                        position: strategy,
                        top: y ?? 0,
                        left: x ?? 0,
                        width: 'max-content'
                    }}
                    {...getFloatingProps()}
                    className={'z-tooltip cursor-pointer menu-popover'}>
                    <div
                        className={'menu-popover__surface'}>
                        {items?.map(item => (
                            <button
                                type={'button'}
                                key={item.id}
                                onClick={e => {
                                    item?.onClick && item?.onClick()
                                    setIsOpen(false)
                                    e?.stopPropagation()
                                }}
                                className={'menu-popover__item'}>
                                <span>{item.text}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default Menu
